import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSim } from "@/context/SimContext";
import StepNav from "@/components/StepNav";
import {
  PREMED_DRUGS,
  INDUCTION_DRUGS,
  MAINTENANCE_AGENTS,
  calcDose,
  calcVolumeForDose,
} from "@/data/drugs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ChevronLeft,
  ChevronRight,
  Pill,
  Syringe,
  Wind,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "—" : Number(n).toFixed(d).replace(/\.?0+$/, "");

const byId = (id) => [...PREMED_DRUGS, ...INDUCTION_DRUGS].find((d) => d.id === id);

const midDose = (drug, species) => {
  if (!drug) return undefined;
  const r = species === "cat" ? drug.doseCat : drug.doseDog;
  if (!r) return undefined;
  return Math.round(((r.low + r.high) / 2) * 1000) / 1000;
};

// Exact-dose selector shown for each chosen drug (defaults to mid-range).
function DosePicker({ drug, species, weight, value, onChange }) {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  const dose = range ? (value ?? (range.low + range.high) / 2) : 0;
  const sliderValue = useMemo(() => [dose], [dose]);
  if (!range) return null;
  const u = range.unit.split("/")[0];
  const span = range.high - range.low;
  const step = span <= 0.5 ? 0.005 : span <= 5 ? 0.05 : 0.5;
  const vol = calcVolumeForDose(drug, weight, dose);
  const total = dose * weight;
  return (
    <div
      className="mt-3 rounded-md border border-[#0047AB]/30 bg-[#0047AB]/[0.06] p-3"
      data-testid={`dose-picker-${drug.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#0047AB] font-semibold">
          Selected dose
        </span>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            value={dose}
            min={range.low}
            max={range.high}
            step={step}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              if (!isNaN(n)) onChange(Math.min(range.high, Math.max(range.low, n)));
            }}
            data-testid={`dose-input-${drug.id}`}
            className="w-24 h-7 font-mono-clinical text-sm"
          />
          <span className="text-xs text-slate-500">{range.unit}</span>
        </div>
      </div>
      <Slider
        value={sliderValue}
        min={range.low}
        max={range.high}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={`dose-slider-${drug.id}`}
      />
      <div className="flex justify-between mt-2 text-[11px]">
        <span className="text-slate-600">
          Total: <b className="font-mono-clinical text-slate-900">{fmt(total, 2)} {u}</b>
        </span>
        <span className="text-[#0047AB]">
          Draw up: <b className="font-mono-clinical">{fmt(vol, 2)} mL</b>
        </span>
      </div>
    </div>
  );
}

function DoseRow({ drug, species, weight, selected, onToggle, dose, onDose }) {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  const supportsSpecies = drug.species.includes(species);
  if (!supportsSpecies) return null;

  const calc = calcDose(drug, weight, species);
  return (
    <div
      className={`border-2 rounded-md p-4 transition-all ${
        selected
          ? "border-[#0047AB] bg-[#0047AB]/5"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`drug-row-${drug.id}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggle(drug.id)}
          className="mt-1"
          data-testid={`drug-toggle-${drug.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-semibold text-slate-900">{drug.name}</div>
              <div className="text-xs text-slate-500">
                {drug.category} · {drug.route}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                Dose range
              </div>
              <div className="font-mono-clinical text-sm text-slate-900">
                {fmt(range.low, 3)}–{fmt(range.high, 3)} {range.unit}
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                Dose for {fmt(weight, 1)} kg
              </div>
              <div className="font-mono-clinical text-base text-slate-900 font-semibold">
                {fmt(calc.lowDose, 2)}–{fmt(calc.highDose, 2)}{" "}
                <span className="text-xs text-slate-500">{range.unit.split("/")[0]}</span>
              </div>
            </div>
            <div className="rounded-md bg-[#0047AB]/5 border border-[#0047AB]/20 p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#0047AB]">
                Volume @ {drug.concentration} mg/mL
              </div>
              <div className="font-mono-clinical text-base text-[#0047AB] font-semibold">
                {fmt(calc.lowMl, 2)}–{fmt(calc.highMl, 2)}{" "}
                <span className="text-xs">mL</span>
              </div>
            </div>
          </div>

          {drug.notes && (
            <div className="mt-2 text-xs text-slate-500 flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{drug.notes}</span>
            </div>
          )}

          {selected && (
            <DosePicker
              drug={drug}
              species={species}
              weight={weight}
              value={dose}
              onChange={onDose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function AnaestheticPlan() {
  const navigate = useNavigate();
  const { patient, plan, setPlan } = useSim();

  useEffect(() => {
    if (!patient) {
      toast.error("Please enter patient details first.");
      navigate("/intake");
    }
  }, [patient, navigate]);

  const [selectedPremeds, setSelectedPremeds] = useState(
    plan?.premeds || ["methadone", "dexmedetomidine"]
  );
  const [induction, setInduction] = useState(plan?.induction || "propofol");
  const [maintenance, setMaintenance] = useState(plan?.maintenance || "isoflurane");

  const sp = patient?.species || "dog";
  const [doses, setDoses] = useState(() => {
    if (plan?.doses) return plan.doses;
    const d = {};
    (plan?.premeds || ["methadone", "dexmedetomidine"]).forEach((id) => {
      const drug = byId(id);
      if (drug) d[id] = midDose(drug, sp);
    });
    const indId = plan?.induction || "propofol";
    const indDrug = byId(indId);
    if (indDrug) d[indId] = midDose(indDrug, sp);
    return d;
  });

  const setDose = (id, val) => setDoses((p) => ({ ...p, [id]: val }));

  const togglePremed = (id) => {
    setSelectedPremeds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setDoses((p) => (p[id] != null ? p : { ...p, [id]: midDose(byId(id), sp) }));
  };

  const onInductionChange = (id) => {
    setInduction(id);
    setDoses((p) => (p[id] != null ? p : { ...p, [id]: midDose(byId(id), sp) }));
  };

  const visiblePremeds = useMemo(
    () => PREMED_DRUGS.filter((d) => d.species.includes(patient?.species || "dog")),
    [patient?.species]
  );
  const visibleInduction = useMemo(
    () =>
      INDUCTION_DRUGS.filter((d) => d.species.includes(patient?.species || "dog")),
    [patient?.species]
  );

  const handleContinue = () => {
    if (selectedPremeds.length === 0) {
      toast.warning("No pre-meds selected. Continuing without premedication.");
    }
    setPlan({
      premeds: selectedPremeds,
      induction,
      maintenance,
      doses,
    });
    navigate("/monitor");
  };

  if (!patient) return null;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav current={2} />
      <main className="max-w-6xl mx-auto px-6 py-10 fade-up">
        <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
              <Syringe className="w-3.5 h-3.5" />
              <span>Anaesthetic protocol</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              Anaesthetic plan
            </h1>
            <p className="text-base text-slate-600 mt-2">
              Doses are weight-calculated from{" "}
              <strong className="text-slate-900 font-mono-clinical">
                {fmt(patient.weightKg, 1)} kg
              </strong>
              .
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md px-4 py-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Patient
            </div>
            <div className="font-semibold text-slate-900">
              {patient.name} · {patient.breed}
            </div>
            <div className="text-xs text-slate-500">
              {patient.species === "cat" ? "Feline" : "Canine"} · ASA {patient.asa} ·{" "}
              {fmt(patient.weightKg, 1)} kg
            </div>
          </div>
        </header>

        <div className="space-y-6">
          {/* Pre-meds */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <Pill className="w-4 h-4 text-[#0047AB]" />
                Pre-medication
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {visiblePremeds.map((d) => (
                <DoseRow
                  key={d.id}
                  drug={d}
                  species={patient.species}
                  weight={patient.weightKg}
                  selected={selectedPremeds.includes(d.id)}
                  onToggle={togglePremed}
                  dose={doses[d.id]}
                  onDose={(v) => setDose(d.id, v)}
                />
              ))}
            </CardContent>
          </Card>

          {/* Induction */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <Syringe className="w-4 h-4 text-[#0047AB]" />
                Induction agent <span className="text-slate-400 text-[10px] normal-case">(select one)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={induction}
                onValueChange={onInductionChange}
                className="grid grid-cols-1 lg:grid-cols-2 gap-3"
                data-testid="induction-radio"
              >
                {visibleInduction.map((d) => {
                  const selected = induction === d.id;
                  return (
                    <div key={d.id}>
                      <label
                        htmlFor={`ind-${d.id}`}
                        className={`cursor-pointer border-2 rounded-md p-4 transition-all block ${
                          selected
                            ? "border-[#0047AB] bg-[#0047AB]/5"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`induction-${d.id}-option`}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value={d.id} id={`ind-${d.id}`} />
                          <div className="flex-1">
                            <DoseRowInline
                              drug={d}
                              species={patient.species}
                              weight={patient.weightKg}
                            />
                          </div>
                        </div>
                      </label>
                      {selected && (
                        <DosePicker
                          drug={d}
                          species={patient.species}
                          weight={patient.weightKg}
                          value={doses[d.id]}
                          onChange={(v) => setDose(d.id, v)}
                        />
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Maintenance */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <Wind className="w-4 h-4 text-[#0047AB]" />
                Maintenance (volatile)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={maintenance}
                onValueChange={setMaintenance}
                className="grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid="maintenance-radio"
              >
                {MAINTENANCE_AGENTS.map((a) => {
                  const selected = maintenance === a.id;
                  const mac = patient.species === "cat" ? a.mac_cat : a.mac_dog;
                  return (
                    <label
                      key={a.id}
                      htmlFor={`mt-${a.id}`}
                      className={`cursor-pointer border-2 rounded-md p-4 transition-all ${
                        selected
                          ? "border-[#0047AB] bg-[#0047AB]/5"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      data-testid={`maintenance-${a.id}-option`}
                    >
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value={a.id} id={`mt-${a.id}`} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <div className="font-semibold text-slate-900">
                                {a.name}
                              </div>
                              <div className="text-xs text-slate-500">
                                {a.category}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                MAC ({patient.species})
                              </div>
                              <div className="font-mono-clinical text-sm text-slate-900">
                                {mac} %
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">
                              Typical maintenance range
                            </div>
                            <div className="font-mono-clinical text-base text-slate-900 font-semibold">
                              {a.typical_range}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-slate-500 flex items-start gap-1.5">
                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{a.notes}</span>
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </CardContent>
          </Card>

          <div className="flex justify-between items-center pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/intake")}
              data-testid="back-to-patient-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Button
              type="button"
              size="lg"
              onClick={handleContinue}
              data-testid="continue-to-monitor-btn"
              className="bg-[#0047AB] hover:bg-[#003380] text-white px-6"
            >
              Begin anaesthesia & start monitoring
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function DoseRowInline({ drug, species, weight }) {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  const calc = calcDose(drug, weight, species);
  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-semibold text-slate-900">{drug.name}</div>
          <div className="text-xs text-slate-500">
            {drug.category} · {drug.route}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Dose range
          </div>
          <div className="font-mono-clinical text-sm text-slate-900">
            {fmt(range.low, 3)}–{fmt(range.high, 3)} {range.unit}
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Dose for {fmt(weight, 1)} kg
          </div>
          <div className="font-mono-clinical text-base text-slate-900 font-semibold">
            {fmt(calc.lowDose, 2)}–{fmt(calc.highDose, 2)}{" "}
            <span className="text-xs text-slate-500">{range.unit.split("/")[0]}</span>
          </div>
        </div>
        <div className="rounded-md bg-[#0047AB]/5 border border-[#0047AB]/20 p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#0047AB]">
            Volume @ {drug.concentration} mg/mL
          </div>
          <div className="font-mono-clinical text-base text-[#0047AB] font-semibold">
            {fmt(calc.lowMl, 2)}–{fmt(calc.highMl, 2)} <span className="text-xs">mL</span>
          </div>
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>{drug.notes}</span>
      </div>
    </>
  );
}
